import { Link } from "react-router-dom";
import { useApiQuery } from "@/api/useApi";
import type { ExpenseEntry, ReportSummary } from "@/api/types";
import { Icon } from "@/components/Icon";
import { HelpText } from "@/components/HelpText";
import { InfoTip } from "@/components/InfoTip";
import { devPreview } from "@/features";
import { useHelp, useShowTips } from "@/help";
import { PendingExpenses } from "@/components/PendingExpenses";
import { ReportCard, ReportListSkeleton } from "@/components/ReportCard";
import { isSentBack, money } from "@/format";

function newestFirst(a: ReportSummary, b: ReportSummary) {
  return b.updatedAt.localeCompare(a.updatedAt);
}

export function HomePage({ userId }: { userId: string }) {
  const help = useHelp();
  const showTips = useShowTips();
  const { data, error, loading, reload } = useApiQuery<ReportSummary[]>("/api/expenses/reports");
  const pending = useApiQuery<ExpenseEntry[]>("/api/expenses/pending");

  const reports = [...(data ?? [])].sort(newestFirst);
  // Sent back first: those are waiting on this person and nobody else.
  const attention = reports
    .filter(
      (r) => r.status === "Draft" || r.status === "Rejected" || r.status === "DelegatorReview",
    )
    .sort((a, b) => Number(isSentBack(b)) - Number(isSentBack(a)));
  const inProgress = reports.filter((r) =>
    ["Submitted", "ManagerApproved", "AccountingApproved"].includes(r.status),
  );
  const paid = reports.filter((r) => r.status === "Paid");
  const owed = inProgress.reduce((sum, r) => sum + r.totalUsd, 0);

  // The same (i) on both New expense report buttons: the header one, and the empty page's.
  const newReportTip = devPreview && showTips && (
    <InfoTip label="About new expense reports">
      <HelpText text={help("newReport")} />
    </InfoTip>
  );

  return (
    <div>
      <div className="page-head">
        <h1>My Reports</h1>
        {reports.length > 0 && (
          <div className="corner-tip-wrap">
            <Link className="btn small" to="/new">
              <Icon name="plus" size={18} /> New expense report
            </Link>
            {newReportTip}
          </div>
        )}
      </div>

      {loading && !data && <ReportListSkeleton />}
      {error && (
        <div className="error" role="alert">
          {error.message}
        </div>
      )}

      <PendingExpenses
        entries={pending.data ?? []}
        userId={userId}
        onChanged={() => {
          pending.reload();
          reload();
        }}
      />

      {data && reports.length === 0 && (
        <div className="card empty">
          <span className="empty-icon">
            <Icon name="receipt" size={28} />
          </span>
          <h2>No reports yet</h2>
          <p className="muted">
            Start one for a trip or event, then add receipts and mileage as you go.
          </p>
          <div className="corner-tip-wrap">
            <Link className="btn" to="/new">
              <Icon name="plus" size={18} /> New expense report
            </Link>
            {newReportTip}
          </div>
        </div>
      )}

      {inProgress.length > 0 && (
        <div className="stat">
          <span className="muted">On its way to you</span>
          <strong>{money(owed)}</strong>
        </div>
      )}

      <Section title="Needs your attention" reports={attention} />
      <Section title="In progress" reports={inProgress} />
      <Section title="Paid" reports={paid} />
    </div>
  );
}

function Section({ title, reports }: { title: string; reports: ReportSummary[] }) {
  if (reports.length === 0) return null;
  return (
    <section className="list-section">
      <h2 className="section-title">{title}</h2>
      {reports.map((report) => (
        <ReportCard key={report.id} report={report} />
      ))}
    </section>
  );
}
